import { CalendarDays, Clock, Layers, Plus, Trash2 } from 'lucide-react';
import type { AvailabilityWindow, JobTimingMode } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export interface JobTimingConstraintsValue {
  timingMode: JobTimingMode;
  serviceDate: string;
  windowStartOffsetSeconds?: number | null;
  windowEndOffsetSeconds?: number | null;
  earliestServiceDate?: string | null;
  deadlineServiceDate?: string | null;
  deadlineOffsetSeconds?: number | null;
  availabilityWindows?: AvailabilityWindow[] | null;
}

export interface JobTimingConstraintsInputProps {
  value: JobTimingConstraintsValue;
  onChange: (value: JobTimingConstraintsValue) => void;
  className?: string;
}

const WINDOW_PRESETS = [
  { label: 'Flexible (Anytime)', start: null, end: null },
  { label: 'Morning (08:00 – 12:00)', start: 28800, end: 43200 },
  { label: 'Early Afternoon (12:00 – 15:00)', start: 43200, end: 54000 },
  { label: 'Late Afternoon (14:00 – 17:00)', start: 50400, end: 61200 },
  { label: 'Standard Day (09:00 – 17:00)', start: 32400, end: 61200 },
];

export function JobTimingConstraintsInput({
  value,
  onChange,
  className,
}: JobTimingConstraintsInputProps) {
  const mode = value.timingMode || 'fixed';

  const setMode = (newMode: JobTimingMode) => {
    onChange({
      ...value,
      timingMode: newMode,
      // Initialize defaults if switching
      earliestServiceDate:
        newMode === 'range'
          ? value.earliestServiceDate || value.serviceDate
          : value.earliestServiceDate,
      deadlineServiceDate:
        newMode === 'range'
          ? value.deadlineServiceDate || value.serviceDate
          : value.deadlineServiceDate,
      availabilityWindows:
        newMode === 'multi_slot' &&
        (!value.availabilityWindows || value.availabilityWindows.length === 0)
          ? [
              {
                id: 'slot-1',
                serviceDate: value.serviceDate,
                windowStartOffsetSeconds: value.windowStartOffsetSeconds ?? 28800,
                windowEndOffsetSeconds: value.windowEndOffsetSeconds ?? 43200,
                label: 'Slot 1',
              },
            ]
          : value.availabilityWindows,
    });
  };

  const handleAddSlot = () => {
    const currentSlots = value.availabilityWindows || [];
    const nextSlotNum = currentSlots.length + 1;
    const newSlot: AvailabilityWindow = {
      id: `slot-${Date.now()}`,
      serviceDate: value.serviceDate,
      windowStartOffsetSeconds: 28800,
      windowEndOffsetSeconds: 43200,
      label: `Slot ${nextSlotNum}`,
    };
    onChange({
      ...value,
      availabilityWindows: [...currentSlots, newSlot],
    });
  };

  const handleRemoveSlot = (index: number) => {
    const currentSlots = value.availabilityWindows || [];
    onChange({
      ...value,
      availabilityWindows: currentSlots.filter((_, idx) => idx !== index),
    });
  };

  const handleUpdateSlot = (index: number, updates: Partial<AvailabilityWindow>) => {
    const currentSlots = value.availabilityWindows || [];
    const updated = currentSlots.map((slot, idx) =>
      idx === index ? { ...slot, ...updates } : slot
    );
    onChange({
      ...value,
      availabilityWindows: updated,
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Mode Switcher Tabs */}
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-foreground">Timing & Availability Mode</Label>
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-muted/40 rounded-xl border border-border/50 text-xs">
          <button
            type="button"
            onClick={() => setMode('fixed')}
            className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg font-medium transition-all ${
              mode === 'fixed'
                ? 'bg-background text-foreground shadow-xs border border-border/60'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <Clock className="h-3.5 w-3.5 text-primary" />
            <span>Fixed Window</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('range')}
            className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg font-medium transition-all ${
              mode === 'range'
                ? 'bg-background text-foreground shadow-xs border border-border/60'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <CalendarDays className="h-3.5 w-3.5 text-amber-500" />
            <span>Multi-Day SLA</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('multi_slot')}
            className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg font-medium transition-all ${
              mode === 'multi_slot'
                ? 'bg-background text-foreground shadow-xs border border-border/60'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <Layers className="h-3.5 w-3.5 text-indigo-500" />
            <span>Customer Slots</span>
          </button>
        </div>
      </div>

      {/* Mode A: Fixed Window */}
      {mode === 'fixed' && (
        <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-3 shadow-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Service Date</Label>
              <Input
                type="date"
                value={value.serviceDate}
                onChange={(e) => onChange({ ...value, serviceDate: e.target.value })}
                className="h-8 text-xs bg-background"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Arrival Window Preset</Label>
              <select
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                value={
                  value.windowStartOffsetSeconds != null && value.windowEndOffsetSeconds != null
                    ? `${value.windowStartOffsetSeconds}-${value.windowEndOffsetSeconds}`
                    : 'flexible'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'flexible') {
                    onChange({
                      ...value,
                      windowStartOffsetSeconds: null,
                      windowEndOffsetSeconds: null,
                    });
                  } else {
                    const [s, end] = val.split('-').map(Number);
                    onChange({
                      ...value,
                      windowStartOffsetSeconds: s,
                      windowEndOffsetSeconds: end,
                    });
                  }
                }}
              >
                {WINDOW_PRESETS.map((p, idx) => (
                  <option key={idx} value={p.start != null ? `${p.start}-${p.end}` : 'flexible'}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Mode B: Multi-Day SLA Range */}
      {mode === 'range' && (
        <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-3 shadow-xs">
          <div className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span>
              The solver can schedule this job on whichever day best clusters with active operator
              routes.
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Earliest Start Date</Label>
              <Input
                type="date"
                value={value.earliestServiceDate || value.serviceDate}
                onChange={(e) =>
                  onChange({
                    ...value,
                    earliestServiceDate: e.target.value,
                    serviceDate: e.target.value,
                  })
                }
                className="h-8 text-xs bg-background"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Deadline / Must Finish By</Label>
              <Input
                type="date"
                value={value.deadlineServiceDate || value.serviceDate}
                onChange={(e) => onChange({ ...value, deadlineServiceDate: e.target.value })}
                className="h-8 text-xs bg-background"
              />
            </div>
          </div>
        </div>
      )}

      {/* Mode C: Customer Availability Slots */}
      {mode === 'multi_slot' && (
        <div className="p-3.5 rounded-xl border border-border/50 bg-card space-y-3 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">
              Candidate Customer Windows
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 text-[11px] px-2 gap-1 text-primary"
              onClick={handleAddSlot}
            >
              <Plus className="h-3 w-3" />
              <span>Add Option</span>
            </Button>
          </div>

          <div className="space-y-2">
            {(value.availabilityWindows || []).map((slot, idx) => (
              <div
                key={slot.id || idx}
                className="flex items-center gap-2 p-2 rounded-lg border border-border/40 bg-muted/20 text-xs"
              >
                <Badge variant="outline" className="text-[10px] shrink-0 font-mono">
                  Option {idx + 1}
                </Badge>

                <Input
                  type="date"
                  value={slot.serviceDate}
                  onChange={(e) => handleUpdateSlot(idx, { serviceDate: e.target.value })}
                  className="h-7 text-xs flex-1 bg-background"
                />

                <select
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring flex-1"
                  value={
                    slot.windowStartOffsetSeconds != null && slot.windowEndOffsetSeconds != null
                      ? `${slot.windowStartOffsetSeconds}-${slot.windowEndOffsetSeconds}`
                      : 'flexible'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'flexible') {
                      handleUpdateSlot(idx, {
                        windowStartOffsetSeconds: null,
                        windowEndOffsetSeconds: null,
                      });
                    } else {
                      const [s, end] = val.split('-').map(Number);
                      handleUpdateSlot(idx, {
                        windowStartOffsetSeconds: s,
                        windowEndOffsetSeconds: end,
                      });
                    }
                  }}
                >
                  {WINDOW_PRESETS.map((p, pIdx) => (
                    <option key={pIdx} value={p.start != null ? `${p.start}-${p.end}` : 'flexible'}>
                      {p.label}
                    </option>
                  ))}
                </select>

                {(value.availabilityWindows || []).length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 shrink-0"
                    onClick={() => handleRemoveSlot(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
